/**
 * The decisions the consumption screen makes, kept out of the component that
 * draws them.
 *
 * Three of them are the reason this file exists at all.
 *
 * **A missing figure is not a zero.** The server answers `null` with a reason
 * beside it, and that is today's ordinary answer for sessions and people rather
 * than a fault. Every figure therefore leaves here as a two-state value that a
 * component cannot render as a number by accident: either it is counted, or it
 * is absent and carries the server's own sentence. Nothing in this file
 * substitutes a zero, a dash or an estimate for an absent figure — somebody is
 * invoiced from these numbers, and zero is a number an invoice can be written
 * from.
 *
 * **A name a node gave itself never leaves here on its own.** `claimed_name` is
 * asserted by the node and checked by nobody, so it comes out of `agentLines`
 * welded to the key that marks it. There is no shape here that carries the name
 * without the mark.
 *
 * **Rows are not people.** `people.rows` is reachable only as the note that
 * explains why the figure looks small next to a month of audit; it is not a
 * field of the line the screen prints as a figure.
 *
 * The contract is `gdsgate-saas`, docs/api.md §6.
 */

import type { ApiError } from '../../api/errors';
import type { SimpleKey } from '../../i18n/types';
import type {
  AgentUsage,
  PeopleUsage,
  ResourceUsage,
  SessionUsage,
  TenantUsage,
  UsageAgent,
  UsageFigureName,
  UsageGap,
  UsagePeriod,
} from '../../api/types';

// ---------------------------------------------------------------------------
// A figure, or the absence of one
// ---------------------------------------------------------------------------

/**
 * A figure that was counted, or one that was not — and there is no third state.
 *
 * `reason` is the server's own sentence. It is optional only because an answer
 * that left a figure out and named no reason is possible on the wire; the screen
 * says exactly that rather than filling the hole with a number.
 */
export type Figure<T> = { counted: true; value: T } | { counted: false; reason: string | undefined };

/** Why a figure is missing, in the server's words. */
export function reasonFor(usage: TenantUsage, figure: UsageFigureName): string | undefined {
  return usage.unavailable.find((gap) => gap.figure === figure)?.reason;
}

function figureOf<T>(
  value: T | null | undefined,
  usage: TenantUsage,
  name: UsageFigureName,
): Figure<T> {
  if (value === null || value === undefined) return { counted: false, reason: reasonFor(usage, name) };
  return { counted: true, value };
}

export function agentsFigure(usage: TenantUsage): Figure<AgentUsage> {
  return figureOf(usage.connectors, usage, 'connectors');
}

export function resourcesFigure(usage: TenantUsage): Figure<ResourceUsage> {
  return figureOf(usage.resources, usage, 'resources');
}

export function sessionsFigure(usage: TenantUsage): Figure<SessionUsage> {
  return figureOf(usage.sessions, usage, 'sessions');
}

export function peopleFigure(usage: TenantUsage): Figure<PeopleUsage> {
  return figureOf(usage.people, usage, 'people');
}

/**
 * Gaps naming something other than the four figures this console draws.
 *
 * Shown rather than dropped: a gap is a hole in what somebody is billed from,
 * and a hole the console silently ignored because it did not recognise the name
 * would be the one nobody ever hears about.
 */
export function unknownGaps(usage: TenantUsage): UsageGap[] {
  const known: string[] = ['connectors', 'resources', 'sessions', 'people'];
  return usage.unavailable.filter((gap) => !known.includes(gap.figure));
}

// ---------------------------------------------------------------------------
// The lines the screen prints
// ---------------------------------------------------------------------------

/** A name the node asserted, with the key that says nobody checked it. */
export interface ClaimedName {
  name: string;
  /** Printed beside the name, every time it is printed. */
  markKey: SimpleKey;
}

/** One connector, as the screen prints it. */
export interface AgentLine {
  /** The registry id — the checked identifier, and what a revocation takes. */
  id: string;
  /** Absent when the node named itself nothing. Never present without its mark. */
  claimed?: ClaimedName;
  /** Absent when the registry printed a stamp that is not an instant. */
  enrolled_at?: string;
}

export function agentLines(connectors: AgentUsage): AgentLine[] {
  return connectors.connectors.map(lineFor);
}

function lineFor(connector: UsageAgent): AgentLine {
  const line: AgentLine = { id: connector.id };
  const claimed = connector.claimed_name;
  if (claimed !== undefined && claimed !== '') {
    line.claimed = { name: claimed, markKey: 'usage.connectors.claimed.mark' };
  }
  if (connector.enrolled_at !== undefined) line.enrolled_at = connector.enrolled_at;
  return line;
}

/**
 * The people figure, and the one number that must never be mistaken for it.
 *
 * `count` is the figure. The rows those people accounted for are reachable only
 * through `rowsNote`, which exists to answer "why is 17 so much smaller than
 * this month's log" — and is not a figure the screen puts in a box of its own.
 */
export interface PeopleLine {
  count: number;
  rowsNote: { key: SimpleKey; rows: number };
}

export function peopleLine(people: PeopleUsage): PeopleLine {
  return { count: people.count, rowsNote: { key: 'usage.people.rows', rows: people.rows } };
}

/** A length in whole seconds, split exactly. No rounding: an invoice is not approximate. */
export function durationParts(seconds: number): { hours: number; minutes: number; seconds: number } {
  const whole = Math.max(0, Math.trunc(seconds));
  return {
    hours: Math.trunc(whole / 3600),
    minutes: Math.trunc((whole % 3600) / 60),
    seconds: whole % 60,
  };
}

/** Kinds in a stable order, so two readings of the same answer do not reshuffle. */
export function byKindEntries(counts: Record<string, number>): { kind: string; count: number }[] {
  return Object.entries(counts)
    .map(([kind, count]) => ({ kind, count }))
    .sort((a, b) => a.kind.localeCompare(b.kind));
}

// ---------------------------------------------------------------------------
// Choosing the period
// ---------------------------------------------------------------------------

/** The two boxes, as a person fills them in: one day each, `YYYY-MM-DD`. */
export interface PeriodForm {
  from: string;
  to: string;
}

export type PeriodField = 'from' | 'to';
export type PeriodErrors = Partial<Record<PeriodField, SimpleKey>>;

const DAY = /^\d{4}-\d{2}-\d{2}$/;

export function emptyPeriodForm(): PeriodForm {
  return { from: '', to: '' };
}

/**
 * What is wrong with the period, before anything is sent.
 *
 * Nothing here is filled in for the person: the server has no default for
 * either end, and a console that supplied one would be choosing the period an
 * invoice is read over on somebody's behalf.
 */
export function checkPeriod(form: PeriodForm): PeriodErrors {
  const errors: PeriodErrors = {};

  const from = form.from.trim();
  const to = form.to.trim();

  if (from === '') errors.from = 'usage.check.fromRequired';
  else if (!readable(from)) errors.from = 'usage.check.fromUnreadable';

  if (to === '') errors.to = 'usage.check.toRequired';
  else if (!readable(to)) errors.to = 'usage.check.toUnreadable';

  if (errors.from === undefined && errors.to === undefined && Date.parse(to) <= Date.parse(from)) {
    errors.to = 'usage.check.order';
  }

  return errors;
}

function readable(day: string): boolean {
  return DAY.test(day) && !Number.isNaN(Date.parse(`${day}T00:00:00Z`));
}

/**
 * The period as the server takes it, or `null` when the choice is incomplete.
 *
 * `null` is what keeps a half-filled form from being sent: there is no shape
 * here that carries one end.
 */
export function periodOf(form: PeriodForm): UsagePeriod | null {
  if (Object.keys(checkPeriod(form)).length > 0) return null;
  return { from: instantOf(form.from.trim()), to: instantOf(form.to.trim()) };
}

/** A day, as the instant it begins. Read as UTC, which the screen says out loud. */
function instantOf(day: string): string {
  return `${day}T00:00:00Z`;
}

/**
 * A whole month, both ends at once.
 *
 * Offered as named buttons rather than as a filled-in default: pressing one is
 * a choice a person made and can see in the boxes afterwards, and the end lands
 * on the first day of the next month because the window does not include it.
 */
export function monthOf(now: Date): PeriodForm {
  return monthStarting(now.getUTCFullYear(), now.getUTCMonth());
}

export function monthBefore(now: Date): PeriodForm {
  return monthStarting(now.getUTCFullYear(), now.getUTCMonth() - 1);
}

function monthStarting(year: number, month: number): PeriodForm {
  return {
    from: dayOf(new Date(Date.UTC(year, month, 1))),
    to: dayOf(new Date(Date.UTC(year, month + 1, 1))),
  };
}

function dayOf(at: Date): string {
  return at.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// When the reading itself is refused
// ---------------------------------------------------------------------------

/**
 * A refusal of the whole reading, in the words that fit it.
 *
 * Two get their own wording. A tenancy that is not there and one this account is
 * not entitled to answer the same `404`, and the screen says the same thing for
 * both — anything finer would turn a refusal into a list of the provider's
 * customers. And a name the tenancy's cluster will not record refuses the
 * reading before it is made, because reading their catalogue lands in their
 * audit trail under the name of whoever asked.
 */
export type UsageFailure =
  | { kind: 'notFound'; titleKey: SimpleKey; bodyKey: SimpleKey }
  | { kind: 'unnameable'; titleKey: SimpleKey; bodyKey: SimpleKey }
  | { kind: 'other'; error: ApiError };

export function usageFailure(error: ApiError): UsageFailure {
  if (error.code === 'tenant_not_found') {
    // The same two keys the tenancy card uses, and deliberately: one wording
    // for "no such tenancy" and "not yours" means there is only one to keep
    // free of hints that the other case exists.
    return { kind: 'notFound', titleKey: 'tenant.notFound.title', bodyKey: 'tenant.notFound.body' };
  }
  if (error.code === 'operator_not_nameable') {
    return {
      kind: 'unnameable',
      titleKey: 'usage.notNameable.title',
      bodyKey: 'usage.notNameable.body',
    };
  }
  return { kind: 'other', error };
}
